import { readPreviewImage } from '@/lib/prospect-preview';
import type { ProspectBundle, ProspectPreviewContent } from '@/types';

type UpdatePreviewContent = <Key extends keyof ProspectPreviewContent>(
  key: Key,
  value: ProspectPreviewContent[Key],
) => void;

type ProspectPreviewContentEditorProps = {
  content: ProspectPreviewContent;
  update: UpdatePreviewContent;
  disabled?: boolean;
  onError: (message: string | null) => void;
};

export function ProspectPreviewContentEditor({
  content,
  update,
  disabled = false,
  onError,
}: ProspectPreviewContentEditorProps) {
  const useImage = async (key: 'logoUrl' | 'coverUrl', file?: File) => {
    if (!file) return;
    try {
      update(key, await readPreviewImage(file));
      onError(null);
    } catch (error) {
      onError(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <div className="form-stack">
      <div className="preview-form-grid">
        <label className="field">
          Business name
          <input
            value={content.businessName}
            onChange={(event) => update('businessName', event.target.value)}
            disabled={disabled}
          />
        </label>
        <label className="field">
          Category
          <input
            value={content.category}
            onChange={(event) => update('category', event.target.value)}
            placeholder="Barbershop"
            disabled={disabled}
          />
        </label>
      </div>
      <div className="preview-form-grid">
        <label className="field">
          Website
          <input
            type="url"
            value={content.websiteUrl ?? ''}
            onChange={(event) => update('websiteUrl', event.target.value)}
            placeholder="https://example.com"
            disabled={disabled}
          />
        </label>
        <label className="field">
          Booking page
          <input
            type="url"
            value={content.bookingUrl ?? ''}
            onChange={(event) => update('bookingUrl', event.target.value)}
            placeholder="https://booking.example.com/business"
            disabled={disabled}
          />
        </label>
        <label className="field">
          Instagram
          <input
            type="url"
            value={content.instagramUrl ?? ''}
            onChange={(event) => update('instagramUrl', event.target.value)}
            placeholder="https://instagram.com/business"
            disabled={disabled}
          />
        </label>
      </div>
      <label className="field">
        Description
        <textarea
          rows={3}
          value={content.description}
          onChange={(event) => update('description', event.target.value)}
          disabled={disabled}
        />
      </label>
      <div className="preview-form-grid">
        <label className="field">
          Address
          <input
            value={content.address}
            onChange={(event) => update('address', event.target.value)}
            disabled={disabled}
          />
        </label>
        <label className="field">
          Currency
          <input
            maxLength={3}
            value={content.currency}
            onChange={(event) => update('currency', event.target.value.toUpperCase())}
            disabled={disabled}
          />
        </label>
      </div>
      <div className="preview-form-grid">
        <label className="field preview-file-field">
          Logo image
          <small>PNG, JPEG or WebP · max 2 MB</small>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(event) => void useImage('logoUrl', event.target.files?.[0])}
            disabled={disabled}
          />
          {content.logoUrl ? (
            <button type="button" onClick={() => update('logoUrl', '')}>Remove</button>
          ) : null}
        </label>
        <label className="field preview-file-field">
          Cover image
          <small>Landscape image · max 2 MB</small>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(event) => void useImage('coverUrl', event.target.files?.[0])}
            disabled={disabled}
          />
          {content.coverUrl ? (
            <button type="button" onClick={() => update('coverUrl', '')}>Remove</button>
          ) : null}
        </label>
      </div>

      <PreviewHoursEditor content={content} update={update} disabled={disabled} />
      <PreviewCategoriesEditor content={content} update={update} disabled={disabled} />
      <PreviewServicesEditor content={content} update={update} disabled={disabled} />
      <PreviewBundlesEditor content={content} update={update} disabled={disabled} />
      <PreviewStaffEditor
        content={content}
        update={update}
        disabled={disabled}
        onError={(message) => onError(message)}
      />
    </div>
  );
}

function PreviewHoursEditor({
  content,
  update,
  disabled,
}: {
  content: ProspectPreviewContent;
  update: UpdatePreviewContent;
  disabled: boolean;
}) {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const hours = content.openingHours ?? [];
  const change = (
    day: number,
    patch: Partial<ProspectPreviewContent['openingHours'][number]>,
  ) => {
    const existing = hours.find((item) => item.day === day) ?? {
      day,
      opensAt: '09:00',
      closesAt: '18:00',
      closed: day === 0,
    };
    update(
      'openingHours',
      Array.from({ length: 7 }, (_, currentDay) => {
        const current = hours.find((item) => item.day === currentDay) ?? {
          day: currentDay,
          opensAt: '09:00',
          closesAt: '18:00',
          closed: currentDay === 0,
        };
        return currentDay === day ? { ...existing, ...patch } : current;
      }),
    );
  };

  return (
    <div className="preview-collection">
      <div className="subpage-actions">
        <div><strong>Opening hours</strong><span>Shown on the preview business page.</span></div>
      </div>
      {Array.from({ length: 7 }, (_, day) => {
        const value = hours.find((item) => item.day === day) ?? {
          day,
          opensAt: '09:00',
          closesAt: '18:00',
          closed: day === 0,
        };
        return (
          <div className="preview-collection-row preview-hours-row" key={day}>
            <strong>{dayNames[day]}</strong>
            <label>
              <input
                type="checkbox"
                checked={value.closed}
                onChange={(event) => change(day, { closed: event.target.checked })}
                disabled={disabled}
              />
              Closed
            </label>
            <input
              aria-label={`${dayNames[day]} opens`}
              type="time"
              value={value.opensAt}
              onChange={(event) => change(day, { opensAt: event.target.value })}
              disabled={disabled || value.closed}
            />
            <input
              aria-label={`${dayNames[day]} closes`}
              type="time"
              value={value.closesAt}
              onChange={(event) => change(day, { closesAt: event.target.value })}
              disabled={disabled || value.closed}
            />
          </div>
        );
      })}
    </div>
  );
}

function PreviewCategoriesEditor({
  content,
  update,
  disabled,
}: {
  content: ProspectPreviewContent;
  update: UpdatePreviewContent;
  disabled: boolean;
}) {
  const categories = content.categories ?? [];
  const change = (index: number, name: string) => {
    update(
      'categories',
      categories.map((item, itemIndex) => (
        itemIndex === index ? { ...item, name } : item
      )),
    );
  };

  return (
    <div className="preview-collection">
      <div className="subpage-actions">
        <div>
          <strong>Categories</strong>
          <span>Section titles on the customer page. Two or more show the tab bar.</span>
        </div>
        <button
          className="button secondary"
          type="button"
          disabled={disabled || categories.length >= 20}
          onClick={() => update('categories', [
            ...categories,
            { id: crypto.randomUUID(), name: 'New category', sortOrder: categories.length },
          ])}
        >
          Add category
        </button>
      </div>
      {categories.map((category, index) => (
        <div className="preview-collection-row preview-category-row" key={category.id}>
          <input
            aria-label="Category name"
            value={category.name}
            onChange={(event) => change(index, event.target.value)}
            disabled={disabled}
          />
          <button
            className="icon-button"
            type="button"
            aria-label="Remove category"
            onClick={() => {
              const next = categories.filter((_, itemIndex) => itemIndex !== index);
              update('categories', next);
              update(
                'services',
                content.services.map((service) => (
                  service.categoryId === category.id ? { ...service, categoryId: null } : service
                )),
              );
              update(
                'bundles',
                (content.bundles ?? []).map((bundle) => (
                  bundle.categoryId === category.id ? { ...bundle, categoryId: null } : bundle
                )),
              );
            }}
            disabled={disabled}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

function PreviewServicesEditor({
  content,
  update,
  disabled,
}: {
  content: ProspectPreviewContent;
  update: UpdatePreviewContent;
  disabled: boolean;
}) {
  const categories = content.categories ?? [];
  const change = (
    index: number,
    patch: Partial<ProspectPreviewContent['services'][number]>,
  ) => {
    update(
      'services',
      content.services.map((item, itemIndex) => (
        itemIndex === index ? { ...item, ...patch } : item
      )),
    );
  };

  const optionalNumber = (value: string) => {
    if (!value.trim()) return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  return (
    <div className="preview-collection">
      <div className="subpage-actions">
        <div>
          <strong>Services</strong>
          <span>Price range and sale use the same customer card as the live listing.</span>
        </div>
        <button
          className="button secondary"
          type="button"
          disabled={disabled || content.services.length >= 200}
          onClick={() => update('services', [
            ...content.services,
            {
              id: crypto.randomUUID(),
              name: 'New service',
              description: '',
              durationMinutes: 30,
              price: 0,
              categoryId: categories[0]?.id ?? null,
              sortOrder: content.services.length,
            },
          ])}
        >
          Add service
        </button>
      </div>
      {content.services.map((service, index) => (
        <div className="preview-service-block" key={service.id}>
          <div className="preview-collection-row preview-service-row">
            <label className="field">
              Name
              <input
                aria-label="Service name"
                value={service.name}
                onChange={(event) => change(index, { name: event.target.value })}
                disabled={disabled}
              />
            </label>
            <label className="field">
              Category
              <select
                aria-label="Service category"
                value={service.categoryId ?? ''}
                onChange={(event) => change(index, { categoryId: event.target.value || null })}
                disabled={disabled}
              >
                <option value="">Other</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </label>
            <button
              className="icon-button preview-service-remove"
              type="button"
              aria-label="Remove service"
              onClick={() => {
                const remaining = content.services.filter((_, itemIndex) => itemIndex !== index);
                update('services', remaining);
                update(
                  'bundles',
                  (content.bundles ?? [])
                    .map((bundle) => ({
                      ...bundle,
                      serviceIds: bundle.serviceIds.filter((id) => id !== service.id),
                    }))
                    .filter((bundle) => bundle.serviceIds.length >= 2),
                );
              }}
              disabled={disabled || content.services.length === 1}
            >
              ×
            </button>
          </div>
          <div className="preview-collection-row preview-service-metrics">
            <label className="field">
              Minutes
              <input
                aria-label="Duration in minutes"
                type="number"
                min={5}
                max={480}
                step={5}
                value={service.durationMinutes}
                onChange={(event) => change(index, { durationMinutes: Number(event.target.value) })}
                disabled={disabled}
              />
            </label>
            <label className="field">
              Price
              <input
                aria-label="Price"
                type="number"
                min={0}
                step="1"
                value={service.price}
                onChange={(event) => change(index, { price: Number(event.target.value) })}
                disabled={disabled}
              />
            </label>
            <label className="field">
              Price to
              <input
                aria-label="Price range maximum"
                type="number"
                min={0}
                step="1"
                placeholder="Optional"
                value={service.priceMax ?? ''}
                onChange={(event) => change(index, { priceMax: optionalNumber(event.target.value) })}
                disabled={disabled}
              />
            </label>
            <label className="field">
              Sale
              <input
                aria-label="Sale price"
                type="number"
                min={0}
                step="1"
                placeholder="Optional"
                value={service.salePrice ?? ''}
                onChange={(event) => change(index, { salePrice: optionalNumber(event.target.value) })}
                disabled={disabled}
              />
            </label>
          </div>
        </div>
      ))}
    </div>
  );
}

function PreviewBundlesEditor({
  content,
  update,
  disabled,
}: {
  content: ProspectPreviewContent;
  update: UpdatePreviewContent;
  disabled: boolean;
}) {
  const categories = content.categories ?? [];
  const bundles = content.bundles ?? [];
  const namedServices = content.services.filter((service) => service.name.trim());
  const change = (index: number, patch: Partial<ProspectBundle>) => {
    update(
      'bundles',
      bundles.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
    );
  };

  return (
    <div className="preview-collection">
      <div className="subpage-actions">
        <div>
          <strong>Bundles</strong>
          <span>One catalog card. Sale is optional; otherwise customers see the sum.</span>
        </div>
        <button
          className="button secondary"
          type="button"
          disabled={disabled || namedServices.length < 2 || bundles.length >= 12}
          onClick={() => update('bundles', [
            ...bundles,
            {
              id: crypto.randomUUID(),
              name: 'New bundle',
              serviceIds: namedServices.slice(0, 2).map((service) => service.id),
              categoryId: categories[0]?.id ?? null,
              sortOrder: bundles.length,
            },
          ])}
        >
          Add bundle
        </button>
      </div>
      {namedServices.length < 2 ? (
        <p className="preview-collection-hint">Save at least two services before creating a bundle.</p>
      ) : null}
      {bundles.map((bundle, index) => (
        <div className="preview-service-block" key={bundle.id}>
          <div className="preview-collection-row preview-bundle-row">
            <label className="field">
              Name
              <input
                aria-label="Bundle name"
                value={bundle.name}
                onChange={(event) => change(index, { name: event.target.value })}
                disabled={disabled}
              />
            </label>
            <label className="field">
              Category
              <select
                aria-label="Bundle category"
                value={bundle.categoryId ?? ''}
                onChange={(event) => change(index, { categoryId: event.target.value || null })}
                disabled={disabled}
              >
                <option value="">Other</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </label>
            <label className="field">
              Sale
              <input
                aria-label="Bundle sale price"
                type="number"
                min={0}
                step=".01"
                value={bundle.salePrice ?? ''}
                onChange={(event) => {
                  const parsed = Number(event.target.value);
                  change(index, {
                    salePrice: event.target.value.trim() && Number.isFinite(parsed) ? parsed : undefined,
                  });
                }}
                disabled={disabled}
              />
            </label>
            <button
              className="icon-button preview-service-remove"
              type="button"
              aria-label="Remove bundle"
              onClick={() => update('bundles', bundles.filter((_, itemIndex) => itemIndex !== index))}
              disabled={disabled}
            >
              ×
            </button>
          </div>
          <div className="preview-bundle-services">
            {namedServices.map((service) => {
              const selected = bundle.serviceIds.includes(service.id);
              return (
                <label key={service.id}>
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => {
                      const serviceIds = selected
                        ? bundle.serviceIds.filter((id) => id !== service.id)
                        : [...bundle.serviceIds, service.id];
                      change(index, { serviceIds });
                    }}
                    disabled={disabled}
                  />
                  {service.name}
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function PreviewStaffEditor({
  content,
  update,
  disabled,
  onError,
}: {
  content: ProspectPreviewContent;
  update: UpdatePreviewContent;
  disabled: boolean;
  onError: (message: string) => void;
}) {
  const change = (
    index: number,
    patch: Partial<ProspectPreviewContent['staff'][number]>,
  ) => {
    update(
      'staff',
      content.staff.map((item, itemIndex) => (
        itemIndex === index ? { ...item, ...patch } : item
      )),
    );
  };

  const staffImage = async (index: number, file?: File) => {
    if (!file) return;
    try {
      change(index, { imageUrl: await readPreviewImage(file) });
    } catch (error) {
      onError(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <div className="preview-collection">
      <div className="subpage-actions">
        <div><strong>Team</strong><span>People who can appear in the booking flow.</span></div>
        <button
          className="button secondary"
          type="button"
          disabled={disabled || content.staff.length >= 40}
          onClick={() => update('staff', [
            ...content.staff,
            {
              id: crypto.randomUUID(),
              name: 'Team member',
              title: 'Specialist',
              imageUrl: '',
            },
          ])}
        >
          Add person
        </button>
      </div>
      {content.staff.map((member, index) => (
        <div className="preview-collection-row preview-staff-row" key={member.id}>
          <span className="prospect-list-mark">
            {member.imageUrl
              ? <img src={member.imageUrl} alt="" />
              : member.name.slice(0, 1).toUpperCase()}
          </span>
          <input
            aria-label="Team member name"
            value={member.name}
            onChange={(event) => change(index, { name: event.target.value })}
            disabled={disabled}
          />
          <label className="button secondary preview-photo-button">
            Photo
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(event) => void staffImage(index, event.target.files?.[0])}
              disabled={disabled}
            />
          </label>
          <button
            className="icon-button"
            type="button"
            aria-label="Remove team member"
            onClick={() => update(
              'staff',
              content.staff.filter((_, itemIndex) => itemIndex !== index),
            )}
            disabled={disabled || content.staff.length === 1}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
